import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, inject } from '@angular/core';
import { FormGroup, UntypedFormArray, UntypedFormControl, FormControl, AbstractControl, Validators, UntypedFormGroup } from '@angular/forms';
import { EmploymentType } from '../../models/enums/employment-type-enum';
import { combineLatest, debounceTime, distinctUntilChanged, filter, first, firstValueFrom, map, Observable, of, shareReplay, startWith, Subject, switchMap, take, takeUntil, tap } from 'rxjs';
import { AgreementTypeDto } from 'src/app/models/dtos/agreement-type-dto';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { DictionaryItem } from '../../models/DictionaryItem';
import { TreeNode } from '../../shared/models/tree-node';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TreeViewerComponent, TreeViewerComponentData } from '../../shared/components/tree-viewer/tree-viewer.component';
import { Messages } from 'src/app/common/enums/messages';
import { Moment } from 'moment';
import moment from 'moment';
import { Permission } from '../../common/enums/permissions';
import { AuthService } from '../../core/authentication/auth.service';
import { AlertDialogComponent } from 'src/app/shared/messages/alert-dialog/alert-dialog.component';
import { WageTypeDto } from 'src/app/models/dtos/wage-type-dto';
import { Wage } from 'src/app/models/WorkerAgreement';
import { MatTableDataSource } from '@angular/material/table';
import { CurrencyEnum } from 'src/app/models/enums/currency-enum';
import { WageDescription } from 'src/app/models/enums/WageDescription';
import { WageValidator } from 'src/app/shared/validators/wage.validator';
import { JobTitleDto } from '../../models/JobTitleDto';

@Component({
  selector: 'app-employment-data-step',
  templateUrl: './employment-data-step.component.html',
  styleUrl: './employment-data-step.component.scss',
  standalone: false
})
export class EmploymentDataStepComponent implements OnInit, OnDestroy, OnChanges {
  private dictionaryService = inject(DictionaryService);
  protected dialog = inject(MatDialog);
  private authService = inject(AuthService);

  @Input() employmentTypeId?: number;
  @Input() companyId?: number;
  @Input() employerObjectId?: number;
  @Input() formGroup: FormGroup;

  protected readonly unsubscribe$ = new Subject<void>();
  protected readonly companyId$ = new Subject<number>();
  protected readonly employmentTypeId$ = new Subject<number>();
  protected readonly EmploymentType = EmploymentType;
  protected readonly timeBetweenInput = 300;
  protected readonly minimumInputLetters = 2;
  protected readonly permittedNumberOfDays = 540;
  protected readonly maxDaysBefore = 7;
  protected readonly miminumWages = 1;
  protected readonly maximumWages = 1;
  private readonly maxWageDescriptionLength = 2000;
  private readonly minWageDescriptionLength = 2;
  protected readonly wageDescriptionsThatCannotOccurrSimultanously: Map<WageDescription, WageDescription[]> = new Map<
    WageDescription,
    WageDescription[]
  >([
    [WageDescription.Daily, [WageDescription.Daily]],
    [WageDescription.Night, [WageDescription.Night]],
  ]);

  public listOfAgreementTypes$: Observable<AgreementTypeDto[]> = of([]);
  public listOfTemporaryWorkplaces$: Observable<DictionaryItem[]> = of([]);
  public listOfOrganizationalUnits$: Observable<DictionaryItem[]> = of([]);

  public listOfWorkingTimeGroup$: Observable<DictionaryItem[]> = of([]);
  public listOfJobTitles$: Observable<JobTitleDto[]> = of([]);
  public listOfWageTypes$: Observable<WageTypeDto[]> = of([]);
  public listOfCurrencies$: Observable<DictionaryItem[]> = of([]);
  public listOfWageDescriptions$: Observable<DictionaryItem[]> = of([]);

  public wageCounter = 0;
  public dataSource = new MatTableDataSource();
  public selectedJobTitleCode$: Observable<string | undefined>;

  listOfWageTypes: WageTypeDto[] = [];
  hasAnyDescriptiveWageType$ = this.listOfWageTypes$.pipe(tap(wts => this.listOfWageTypes = wts), map(wts => wts.some(wt => wt.IsDescriptiveType)));
  canAddWage$: Observable<boolean>;

  constructor() { }

  get temporaryWorkplace(): UntypedFormControl {
    return this.formGroup.get('temporaryWorkplace') as UntypedFormControl;
  }
  get organizationalUnit() {
    return this.formGroup.get('organizationalUnit') as UntypedFormControl;
  }
  get employmentDateFrom() {
    return this.formGroup.get('employmentDateFrom') as UntypedFormControl;
  }
  get employmentDateTo() {
    return this.formGroup.get('employmentDateTo') as UntypedFormControl;
  }
  get wages() {
    return this.formGroup.get('wages') as UntypedFormArray;
  }
  get agreementType() {
    return this.formGroup.get('agreementType') as UntypedFormControl;
  }
  get jobTitle() {
    return this.formGroup.get('jobTitle') as UntypedFormControl;
  }
  get jobTitleCode() {
    return this.formGroup.get('jobTitleCode') as UntypedFormControl;
  }

  get isFormReadyToSubmit(): boolean {
    return this.formGroup.valid;
  }

  ngOnInit(): void {
    this.listOfOrganizationalUnits$ = combineLatest([
      this.companyId$.pipe(startWith(this.companyId)),
      this.employmentTypeId$.pipe(startWith(this.employmentTypeId))
    ])
      .pipe(
        distinctUntilChanged(),
        switchMap(([companyId, employmentTypeId]) => {
          if (!companyId || !employmentTypeId) {
            return of([]);
          }

          return this.dictionaryService
            .getOrganizationalUnits(companyId, employmentTypeId)
            .pipe(
              map((organizationalUnits: DictionaryItem[]) =>
                organizationalUnits.map((ou) => <TreeNode>{
                  ...ou,
                  Checked: false,
                  Expanded: false
                })
              )
            );
        }),
        tap((units: DictionaryItem[]) => {
          if (this.organizationalUnit && !units.some(unit => unit.Id === this.organizationalUnit.value?.Id)) {
            this.organizationalUnit.setValue(null);
          }
        }),
        shareReplay(1)
      );

    this.initializeEmploymentFormData();
    this.onOrganizationalUnitChange();
    this.onTemporaryWorkplaceChange();

    this.listOfWageTypes$ = this.agreementType.valueChanges.pipe(
      startWith(this.agreementType.value),
      distinctUntilChanged(),
      filter(value => !!value),
      switchMap(value => this.dictionaryService.getWageTypesByParameters(value, this.employerObjectId)),
      tap(() => {
        if (!this.wages?.length) {
          this.addNewWage();
        }
      }),
      shareReplay(1)
    );

    this.selectedJobTitleCode$ = combineLatest([
      this.jobTitle.valueChanges.pipe(startWith(this.jobTitle.value)),
      this.listOfJobTitles$.pipe(startWith([]))])
      .pipe(takeUntil(this.unsubscribe$), map(([selectedId, jobs]) => jobs.find((j) => j.Id === selectedId)?.SuggestedJobTitleCode ?? '')
      );

    this.selectedJobTitleCode$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(code => {
        this.jobTitleCode.patchValue(code, { emitEvent: true });
        this.formGroup.updateValueAndValidity();
      });

    this.canAddWage$ = combineLatest([
      this.wages.valueChanges.pipe(startWith(this.wages.value)),
      this.listOfWageTypes$
    ]).pipe(
      map(([wages, wageTypes]) => {
        if (this.isEmploymentAgreementType()) {
          return wages.length < 1;
        }
        return wages.length < wageTypes.length;
      })
    );
  }

  public displayValue = (value: DictionaryItem): string | undefined => value?.Name;

  public onTemporaryWorkplaceChange(): void {
    this.listOfTemporaryWorkplaces$ = this.temporaryWorkplace.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      debounceTime(this.timeBetweenInput),
      distinctUntilChanged(),
      switchMap((value: string) => {
        if (value && value.length > this.minimumInputLetters) {
          return this.dictionaryService.getTemporaryWorkplaces(value);
        } else {
          return of(null);
        }
      }),
    );
  }

  public async onOrganizationalUnitClick(): Promise<void> {
    if (!this.companyId) {
      return;
    }

    const res = await firstValueFrom(this.dialog.open(TreeViewerComponent, this.createDialogConfig()).afterClosed().pipe(first()));

    if (res) {
      this.organizationalUnit.setValue(<DictionaryItem>{ Id: res.id, Name: res.name });
      this.dialog.closeAll();
    }
  }

  protected createDialogConfig(): MatDialogConfig<TreeViewerComponentData> {
    return {
      disableClose: false,
      autoFocus: false,
      panelClass: 'standard-dialog',
      data: {
        header: Messages.ChooseOrganizationalUnit,
        emptyTreeMessage: Messages.EmptyOrganizationalUnitTree,
        datasource$: this.listOfOrganizationalUnits$,
      },
    };
  }

  public onOrganizationalUnitChange(): void {
    this.listOfJobTitles$ = this.organizationalUnit.valueChanges.pipe(
      takeUntil(this.unsubscribe$),
      distinctUntilChanged(),
      switchMap((value: DictionaryItem) => {
        if (!value || !value.Id) return of([]);

        return this.dictionaryService.getJobTitles(null, value.Id);
      }),
    );

    this.organizationalUnit.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.jobTitle.setValue(null);
        this.jobTitleCode.setValue(null);
      });
  }

  public employmentDateFromFilter = (date: Moment): boolean =>
    (this.disableAgreementDateLimit || date?.isSameOrAfter(moment().subtract(this.maxDaysBefore, 'days').startOf('day'))) &&
    (!this.employmentDateTo.value || date.isSameOrBefore(this.employmentDateTo.value));

  public employmentDateToFilter = (date: Moment): boolean => !this.employmentDateFrom.value || date.isSameOrAfter(this.employmentDateFrom.value);

  public checkEmploymentDays(): void {
    if (this.employmentTypeId === EmploymentType.TemporaryEmploymentAgreement &&
      this.employmentDateTo.value &&
      this.employmentDateFrom.value) {

      const diff = this.employmentDateTo.value.valueOf() - this.employmentDateFrom.value.valueOf();
      const diffOfDays = Math.ceil(diff / (1000 * 3600 * 24));

      if (diffOfDays > this.permittedNumberOfDays) {
        this.openAlertDialog(Messages.Exceeded540DaysMessage);
      }
    }
  }

  public async addNewWage(wage: Wage = { Value: null, WageTypeId: null, WageDescriptionId: null, CurrencyId: null } as Wage) {
    const canAdd = await firstValueFrom(this.canAddWage$);
    if (!canAdd) {
      return;
    }

    this.wages.push(this.newWage(wage));
    this.dataSource.data = this.wages.controls;
    this.wageCounter++;
  }

  public deleteWage(index: number): void {
    this.wages.removeAt(index);
    this.dataSource.data = this.wages.controls;
    this.wageCounter--;
  }

  public resetWages(): void {
    while (this.wages.length !== 0) {
      this.wages.removeAt(0)
      this.wageCounter = this.wages.length;
    }

    this.dataSource.data = undefined;
  }



  public canDeleteWage(): boolean {
    return this.wageCounter > this.miminumWages;
  }

  private isEmploymentAgreementType(): boolean {
    return this.employmentTypeId === EmploymentType.EmploymentAgreement ||
      this.employmentTypeId === EmploymentType.TemporaryEmploymentAgreement;
  }

  public isDescriptiveType(wageTypeId: number) {
    return this.listOfWageTypes.find(wts => wts.Id === wageTypeId)?.IsDescriptiveType;
  }

  public getWageTypeName(wageTypeId: number) {
    return this.listOfWageTypes.find(wts => wts.Id === wageTypeId)?.Name;
  }

  public filterWageDescriptions(wageDescriptions: DictionaryItem[], index: number) {
    if (!wageDescriptions || !this.wages.value) {
      return wageDescriptions;
    }

    const isItemAvailable = (item: DictionaryItem): boolean => {
      return this.wages.value.every((wage) => {
        if (!this.wageDescriptionsThatCannotOccurrSimultanously.has(wage.wageDescriptionId)) {
          return true;
        }
        return !this.wageDescriptionsThatCannotOccurrSimultanously
          .get(wage.wageDescriptionId)
          .some((sw) => sw == item.Id && this.wages.value.indexOf(wage) !== index);
      });
    };

    return wageDescriptions.filter((item: DictionaryItem) => isItemAvailable(item));
  }

  public newWage(wage: Wage = null) {
    const wageForm = new FormGroup({
      value: new FormControl(
        wage?.Value,
        [Validators.required],
        [
          (control: AbstractControl) => WageValidator.maxWage(this.listOfWageTypes$, control),
          (control: AbstractControl) => WageValidator.minWage(this.listOfWageTypes$, control),
        ],
      ),
      currencyId: new FormControl(
        wage?.CurrencyId ?? CurrencyEnum.PLN, Validators.required
      ),
      wageTypeId: new FormControl(
        wage?.WageTypeId, Validators.required
      ),
      wageDescriptionId: new FormControl(
        wage?.WageDescriptionId
      ),
      name: new FormControl(
        wage?.Name, [Validators.required, Validators.minLength(this.minWageDescriptionLength), Validators.maxLength(this.maxWageDescriptionLength)]
      ),
      calculationDescription: new FormControl(
        wage?.CalculationDescription, [Validators.required, Validators.minLength(this.minWageDescriptionLength), Validators.maxLength(this.maxWageDescriptionLength)]
      ),
    });

    const wageTypeControl = () => wageForm.get('wageTypeId');
    const wageValueControl = () => wageForm.get('value');

    combineLatest([wageForm.valueChanges, this.listOfWageTypes$])
      .pipe(debounceTime(this.timeBetweenInput), takeUntil(this.unsubscribe$))
      .subscribe(([changedWage, wageTypes]) => {
        const wageType = wageTypes.find((wt) => wt.Id == changedWage.wageTypeId);
        if (!wageType?.IsDescriptiveType) {
          const wageTypeMax = wageType?.MaxWageValue;
          const wageTypeMin = wageType?.MinWageValue;
          if (!!wageTypeMax && wageTypeMax <= changedWage.value) {
            this.openAlertDialog(Messages.ExceededMaxWageMessage);
          }
          if (!!wageTypeMin && wageTypeMin > changedWage.value) {
            this.openAlertDialog(Messages.NotExceededMinWageMessage);
          }
          wageValueControl()?.updateValueAndValidity({ emitEvent: false });
        }
      });

    combineLatest([
      wageTypeControl()?.valueChanges.pipe(startWith(wageTypeControl()?.value)),
      this.listOfWageTypes$
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(([changedWageTypeId, wageTypes]) => {
        const wageType = wageTypes.find((wt) => wt.Id == changedWageTypeId);
        if (!wageType?.IsDescriptiveType) {
          this.setValueWageTypeValidators(wageForm);
        } else {
          this.setDescriptiveWageTypeValidators(wageForm);
        }
      });

    return wageForm;
  }

  private setDescriptiveWageTypeValidators(wageForm: UntypedFormGroup) {
    wageForm.get('value').reset();
    wageForm.get('currencyId').reset();
    wageForm.get('value').disable();
    wageForm.get('currencyId').disable();
    wageForm.get('name').enable();
    wageForm.get('calculationDescription').enable();

    wageForm.updateValueAndValidity({ emitEvent: false });
  }

  private setValueWageTypeValidators(wageForm: UntypedFormGroup) {
    wageForm.get('value').enable();
    wageForm.get('currencyId').enable();
    wageForm.get('name').reset();
    wageForm.get('calculationDescription').reset();
    wageForm.get('name').disable();
    wageForm.get('calculationDescription').disable();

    wageForm.updateValueAndValidity({ emitEvent: false });
  }

  private openAlertDialog = (message: string): void => {
    this.dialog.open(AlertDialogComponent, {
      data: {
        message: message,
      },
    });
  };

  private get disableAgreementDateLimit(): boolean {
    return this.authService.hasPermission(Permission.DisableAgreementDateLimit);
  }

  private initializeEmploymentFormData(): void {
    this.listOfWageDescriptions$ = this.dictionaryService.getWageDescriptions();
    this.listOfCurrencies$ = this.dictionaryService.getCurrencies();
    this.listOfAgreementTypes$ = this.dictionaryService.getAgreementTypes([this.employmentTypeId]);
    this.listOfWorkingTimeGroup$ = this.dictionaryService.getWorkingTimeGroups();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const employmentTypeIdChange = changes.employmentTypeId;
    if (employmentTypeIdChange && employmentTypeIdChange.currentValue) {
      this.employmentTypeId$.next(employmentTypeIdChange.currentValue);
      setTimeout(() => this.resetForm());
    }

    const companyChange = changes.companyId;
    if (companyChange && companyChange.currentValue) {
      this.companyId$.next(companyChange.currentValue);
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private resetForm(): void {
    this.resetWages();

    Object.values(this.formGroup.controls).forEach(control => {
      control.reset();
    });
  }
}